import {
  AlertOutlined,
  CloudOutlined,
  DashboardOutlined,
  CloudServerOutlined,
  ApartmentOutlined,
  BranchesOutlined,
  HddOutlined,
  TeamOutlined,
  DollarOutlined,
  DeploymentUnitOutlined,
  PictureOutlined,
  GlobalOutlined,
  ClusterOutlined,
  FileTextOutlined,
  MonitorOutlined,
  BuildOutlined,
  KeyOutlined,
  InboxOutlined,
  DatabaseOutlined,
  SafetyOutlined,
  SafetyCertificateOutlined,
  CodeOutlined,
  LockOutlined,
  ToolOutlined,
  ThunderboltOutlined,
  FunctionOutlined,
  MessageOutlined,
  NotificationOutlined,
  SwapOutlined,
  EyeOutlined,
  PlaySquareOutlined,
  LinkOutlined,
  FolderOpenOutlined,
  HistoryOutlined,
  FileSearchOutlined,
  ClockCircleOutlined,
  RiseOutlined,
  RobotOutlined,
  GroupOutlined,
  ControlOutlined,
  SecurityScanOutlined,
  FundOutlined,
  RocketOutlined,
  AuditOutlined,
  AppstoreOutlined,
  FileProtectOutlined,
  ProfileOutlined,
} from "@ant-design/icons";

import type { ProductView } from "@/features/sase/types";

export interface MenuRoute {
  path?: string;
  name?: string;
  /** The page's name out of its menu context (the launcher, the browser tab)
   *  when `name` alone would be ambiguous there ("Overview"). */
  launcherName?: string;
  icon?: React.ReactNode;
  routes?: MenuRoute[];
}

/** The SASE section's category key (SASE plan SASE-1, SD-01). */
export const SASE_CATEGORY = "/cat/sase";
/** SASE → AI security, a group inside the section (SASE-18). */
export const SASE_AI_CATEGORY = "/cat/sase-ai";
/** SASE → Data protection, a group inside the section (SASE-24: Exact data first). */
export const SASE_DATA_CATEGORY = "/cat/sase-data";

// Customer sections, grouped by service category (AWS/GCP-style). IMPORTANT:
// every node — including category parents — has a `path`, because this ProLayout
// drops menu nodes that lack one (which is what hid the nav last time). Category
// parents use a synthetic `/cat/*` key: they're never navigated (clicking a
// parent with children expands it), they only need a path to render. The real
// pages are the children.
const customerRoutes: MenuRoute[] = [
  { path: "/", name: "Dashboard", icon: <DashboardOutlined /> },
  {
    path: "/cat/compute",
    name: "Compute",
    icon: <CloudServerOutlined />,
    routes: [
      { path: "/compute", name: "Instances", icon: <CloudServerOutlined /> },
      // Optional on a given cloud: dropped from the menu when the
      // capability probe says no (see buildMenuRoutes).
      {
        path: "/compute/autoscaling-groups",
        name: "Auto scaling groups",
        icon: <RiseOutlined />,
      },
      {
        path: "/compute/scheduled-actions",
        name: "Scheduled actions",
        icon: <ClockCircleOutlined />,
      },
      {
        path: "/compute/placement-groups",
        name: "Placement groups",
        icon: <GroupOutlined />,
      },
      {
        path: "/compute/launch-templates",
        name: "Launch templates",
        icon: <ProfileOutlined />,
      },
      { path: "/functions", name: "Functions", icon: <FunctionOutlined /> },
      // Optional on a given cloud (dropped when the capability probe says no).
      { path: "/agent-gateways", name: "Agent gateways", icon: <RobotOutlined /> },
      { path: "/k8s", name: "Kubernetes", icon: <ClusterOutlined /> },
      {
        path: "/keypairs",
        name: "SSH keys",
        icon: <SafetyCertificateOutlined />,
      },
    ],
  },
  {
    path: "/cat/storage",
    name: "Storage",
    icon: <HddOutlined />,
    routes: [
      { path: "/storage", name: "Volumes", icon: <HddOutlined /> },
      { path: "/images", name: "Images", icon: <PictureOutlined /> },
      { path: "/objects", name: "Object storage", icon: <InboxOutlined /> },
      // Optional on a given cloud: dropped from the menu when the
      // capability probe says no (see buildMenuRoutes).
      { path: "/shares", name: "File shares", icon: <FolderOpenOutlined /> },
      // Needs volume backups and schedules (capabilities); see buildMenuRoutes.
      { path: "/backup-plans", name: "Backup plans", icon: <HistoryOutlined /> },
    ],
  },
  {
    path: "/cat/networking",
    name: "Networking",
    icon: <ApartmentOutlined />,
    routes: [
      { path: "/network", name: "Networks", icon: <ApartmentOutlined /> },
      {
        path: "/lbaas",
        name: "Load balancers",
        icon: <DeploymentUnitOutlined />,
      },
      { path: "/dns", name: "DNS", icon: <GlobalOutlined /> },
      // Optional on a given cloud: dropped from the menu when the
      // capability probe says no (see buildMenuRoutes).
      { path: "/network-acls", name: "Network ACLs", icon: <SafetyOutlined /> },
      { path: "/flow-logs", name: "Flow logs", icon: <FileSearchOutlined /> },
      { path: "/traffic-mirrors", name: "Traffic mirroring", icon: <EyeOutlined /> },
      // Content delivery (plan CDN-4): dropped until the cloud offers it (a
      // delivery node is enrolled); it stays through a node outage.
      { path: "/content-delivery", name: "Content delivery", icon: <CloudServerOutlined /> },
    ],
  },
  {
    // Naming Pass 9: a group of its own after Networking; widen or rename it
    // the day a second media product joins.
    path: "/cat/media",
    name: "Media",
    icon: <PlaySquareOutlined />,
    routes: [
      // Video streaming (plan VID-7): dropped until the cloud offers it.
      { path: "/video-streaming", name: "Video streaming", icon: <PlaySquareOutlined /> },
    ],
  },
  {
    path: "/cat/access",
    name: "Access",
    icon: <SafetyCertificateOutlined />,
    routes: [
      // Optional on a given cloud (dropped when the capability probe says no).
      { path: "/access", name: "Private access", icon: <SafetyCertificateOutlined /> },
      // PA-21: the site-to-site VPN connections live under Access. Menu and
      // copy only: the route stays /vpn (and /vpn/:id), the API is unchanged.
      // Optional (dropped when the capability probe says no).
      { path: "/vpn", name: "VPN", icon: <LinkOutlined /> },
    ],
  },
  {
    path: "/cat/databases",
    name: "Databases",
    icon: <DatabaseOutlined />,
    routes: [
      { path: "/database", name: "Databases", icon: <DatabaseOutlined /> },
      // Optional on a given cloud: dropped until the engine's settings are
      // set up (capabilities.database.parameter_groups, expose plan EX-3).
      {
        path: "/database/parameter-groups",
        name: "Parameter groups",
        icon: <ControlOutlined />,
      },
    ],
  },
  {
    path: "/cat/integration",
    name: "Messaging",
    icon: <MessageOutlined />,
    routes: [
      { path: "/queues", name: "Queues", icon: <MessageOutlined /> },
      { path: "/topics", name: "Notifications", icon: <NotificationOutlined /> },
      { path: "/event-rules", name: "Event rules", icon: <BranchesOutlined /> },
    ],
  },
  {
    // Customer-facing observability (distinct from the admin-only
    // /monitoring hypervisor page under Administration).
    path: "/cat/monitoring",
    name: "Monitoring",
    icon: <MonitorOutlined />,
    routes: [{ path: "/alarms", name: "Alarms", icon: <AlertOutlined /> }],
  },
  {
    path: "/cat/security",
    name: "Security",
    icon: <LockOutlined />,
    routes: [
      { path: "/secrets", name: "Secrets", icon: <KeyOutlined /> },
      { path: "/credentials", name: "Credentials", icon: <CodeOutlined /> },
    ],
  },
  {
    path: "/cat/management",
    name: "Management",
    icon: <ToolOutlined />,
    routes: [
      {
        path: "/orchestration",
        name: "Blueprints",
        icon: <BuildOutlined />,
      },
      { path: "/billing", name: "Billing", icon: <DollarOutlined /> },
      { path: "/audit", name: "Audit log", icon: <FileTextOutlined /> },
    ],
  },
  {
    // SASE (plan §7.2): complete on its own. Every leaf is gated by the `sase`
    // capability; a module's page joins only once it is built (SASE-7…), never
    // as "coming soon". Private access and Agent gateways are second doors onto
    // the cloud's own modules (SD-01, SASE-18): gated by `sase` AND the
    // module's flag, their pages keep every link under /sase.
    path: SASE_CATEGORY,
    name: "SASE",
    icon: <SecurityScanOutlined />,
    routes: [
      { path: "/sase", name: "Overview", launcherName: "SASE overview", icon: <FundOutlined /> },
      {
        path: "/sase/get-started",
        name: "Get started",
        launcherName: "SASE: get started",
        icon: <RocketOutlined />,
      },
      {
        path: "/sase/policies",
        name: "Policies",
        launcherName: "SASE: Policies",
        icon: <AuditOutlined />,
      },
      {
        // SASE-6: devices, sites and tunnels, the proxy and DNS (plan §7.3), gated by `sase`.
        path: "/sase/connections",
        name: "Connections",
        launcherName: "SASE: Connections",
        icon: <LinkOutlined />,
      },
      {
        // SASE-8: Internet access (plan §7.3), gated by `sase`.
        path: "/sase/internet",
        name: "Internet access",
        launcherName: "SASE: Internet access",
        icon: <GlobalOutlined />,
      },
      // SASE-9/10: on the one policy, gated by `sase` AND the module.
      {
        path: "/sase/dns",
        name: "DNS protection",
        launcherName: "SASE: DNS protection",
        icon: <GlobalOutlined />,
      },
      {
        path: "/sase/firewall",
        name: "Firewall",
        launcherName: "SASE: Firewall",
        icon: <SafetyOutlined />,
      },
      {
        path: "/sase/private-access",
        name: "Private access",
        launcherName: "SASE: Private access",
        icon: <SafetyCertificateOutlined />,
      },
      {
        // SASE-20: the cloud-app catalog and risk index (plan §7.3 Cloud apps).
        path: "/sase/cloud-apps",
        name: "Cloud apps",
        launcherName: "SASE: Cloud apps",
        icon: <CloudOutlined />,
      },
      {
        // Data protection (plan §7.3, SASE-24): its tabs are pages of their own, like
        // AI security's; Exact data is the first, behind `sase` AND the module.
        path: SASE_DATA_CATEGORY,
        name: "Data protection",
        icon: <FileProtectOutlined />,
        routes: [
          {
            path: "/sase/data/exact",
            name: "Exact data",
            launcherName: "SASE: Exact data",
            icon: <DatabaseOutlined />,
          },
          {
            // SASE-24 (P37): reference documents recognised by their fingerprints
            path: "/sase/data/fingerprints",
            name: "Document fingerprints",
            launcherName: "SASE: Document fingerprints",
            icon: <FileSearchOutlined />,
          },
        ],
      },
      // SASE-11, gated by `sase` AND the module.
      {
        path: "/sase/threats",
        name: "Threat protection",
        launcherName: "SASE: Threat protection",
        icon: <AlertOutlined />,
      },
      {
        // AI security (plan §7.3, SASE-13): its tabs are pages of their own, so
        // each has a link, a highlight and a place in the launcher; Agent
        // gateways is the cloud module's SASE door (SASE-18).
        path: SASE_AI_CATEGORY,
        name: "AI security",
        icon: <RobotOutlined />,
        routes: [
          {
            path: "/sase/ai",
            name: "Overview",
            launcherName: "SASE: AI security",
            icon: <FundOutlined />,
          },
          {
            path: "/sase/ai/apps",
            name: "AI apps",
            launcherName: "SASE: AI apps",
            icon: <AppstoreOutlined />,
          },
          {
            path: "/sase/ai/prompt-protection",
            name: "Prompt protection",
            launcherName: "SASE: Prompt protection",
            icon: <SafetyCertificateOutlined />,
          },
          // SASE-34 (B20): conversation checks, injection alerts, MCP servers
          {
            path: "/sase/ai/conversations",
            name: "Conversations and injection",
            launcherName: "SASE: Conversations and injection",
            icon: <MessageOutlined />,
          },
          {
            path: "/sase/ai/mcp-servers",
            name: "MCP servers",
            launcherName: "SASE: MCP servers",
            icon: <ToolOutlined />,
          },
          {
            path: "/sase/ai/agent-gateways",
            name: "Agent gateways",
            launcherName: "SASE: Agent gateways",
            icon: <RobotOutlined />,
          },
        ],
      },
      {
        path: "/sase/users",
        name: "Users and devices",
        launcherName: "SASE: Users and devices",
        icon: <TeamOutlined />,
      },
      {
        // SASE-29 (B25): app experience from each location, behind `sase` AND the module.
        path: "/sase/experience",
        name: "Experience",
        launcherName: "SASE: Experience",
        icon: <MonitorOutlined />,
      },
      // SASE-14: the section's own modules, each behind `sase` AND its flag.
      {
        path: "/sase/incidents",
        name: "Incidents",
        launcherName: "SASE: Incidents",
        icon: <AlertOutlined />,
      },
      {
        path: "/sase/analytics",
        name: "Analytics",
        launcherName: "SASE: Analytics",
        icon: <FileSearchOutlined />,
      },
      {
        // SASE-2 (plan §7.3 Settings): the organisation and its inspection certificate.
        path: "/sase/settings",
        name: "Settings",
        launcherName: "SASE: Settings",
        icon: <ControlOutlined />,
      },
    ],
  },
];

// Operator-only category. Same path-bearing rule.
const adminRoutes: MenuRoute[] = [
  {
    path: "/cat/admin",
    name: "Administration",
    icon: <SafetyOutlined />,
    routes: [
      { path: "/identity", name: "Users & access", icon: <TeamOutlined /> },
      { path: "/machines", name: "Machines", icon: <ClusterOutlined /> },
      { path: "/maintenance", name: "Maintenance", icon: <SwapOutlined /> },
      // Phase 7: where agent gateways and their agents run (only with agent gateways).
      { path: "/agent-gateway-fleet", name: "Agent gateway fleet", icon: <RobotOutlined /> },
      // SASE-4: the SASE service's locations and nodes (only with the SASE section).
      { path: "/points-of-presence", name: "Points of presence", icon: <GlobalOutlined /> },
      { path: "/optimization", name: "Optimization", icon: <ThunderboltOutlined /> },
      { path: "/monitoring", name: "Platform metrics", icon: <MonitorOutlined /> },
      // Optional on a given cloud: dropped from the menu when the
      // capability probe says central logging is not configured.
      { path: "/logs", name: "Logs", icon: <FileSearchOutlined /> },
    ],
  },
];

/** Menu leaves that exist only when the cloud has the matching service —
 *  keyed by path; false hides the leaf. Defaults to "everything", which is
 *  right for labelling (a bookmark to a hidden page still needs its name). */
export interface MenuFeatures {
  /** Auto scaling groups (Compute). */
  autoScaling: boolean;
  /** Scheduled actions (Compute). */
  scheduledActions: boolean;
  vpn: boolean;
  acls: boolean;
  flowLogs: boolean;
  /** Traffic mirroring (Networking). */
  trafficMirrors: boolean;
  /** Content delivery (Networking; capabilities.network.content_delivery_offered, CDN-4). */
  contentDelivery?: boolean;
  /** Video streaming (Media; capabilities.media.video_streaming, VID-7). */
  videoStreaming?: boolean;
  shares: boolean;
  /** Backup plans (Storage): volume backups + scheduled actions. */
  backupPlans?: boolean;
  logs: boolean;
  /** Agent gateways (Compute). */
  agentGateways: boolean;
  /** Database parameter groups (expose plan EX-3). */
  parameterGroups?: boolean;
  /** Private access (Access). */
  privateAccess: boolean;
  /** The SASE section (capabilities.sase.enabled, SASE-1). */
  sase?: boolean;
  /** SASE → Analytics (capabilities.sase.modules.analytics, SASE-14). */
  saseAnalytics?: boolean;
  /** SASE → Incidents (capabilities.sase.modules.incidents, SASE-14). */
  saseIncidents?: boolean;
  /** SASE modules (capabilities.sase.modules.*, SASE-9/10/11). */
  saseDnsProtection?: boolean;
  saseFirewall?: boolean;
  saseThreatProtection?: boolean;
  /** SASE → AI security's own pages (capabilities.sase.modules.ai_security, SASE-13). */
  saseAiSecurity?: boolean;
  /** The standalone SASE edition (capabilities.sase.edition === "sase", SASE-30):
   *  SASE, Administration's SASE pages and the account menu, nothing else. */
  saseEdition?: boolean;
  /** SASE → Data protection's pages (capabilities.sase.modules.data_protection, SASE-24). */
  saseDataProtection?: boolean;
  /** SASE → Experience (capabilities.sase.modules.experience, SASE-29). */
  saseExperience?: boolean;
}

const ALL_FEATURES: MenuFeatures = {
  autoScaling: true,
  scheduledActions: true,
  vpn: true,
  acls: true,
  flowLogs: true,
  trafficMirrors: true,
  contentDelivery: true,
  videoStreaming: true,
  shares: true,
  backupPlans: true,
  logs: true,
  agentGateways: true,
  parameterGroups: true,
  privateAccess: true,
  sase: true,
  saseAnalytics: true,
  saseIncidents: true,
  saseDnsProtection: true,
  saseFirewall: true,
  saseThreatProtection: true,
  saseAiSecurity: true,
  saseDataProtection: true,
  saseExperience: true,
};

/** A leaf's gate: one flag, or several that must all be on (a SASE door
 *  needs `sase` AND its module, plan §7.1 rule 4). */
type Gate = keyof MenuFeatures | (keyof MenuFeatures)[];

const OPTIONAL_LEAVES: Record<string, Gate> = {
  "/compute/autoscaling-groups": "autoScaling",
  "/compute/scheduled-actions": "scheduledActions",
  "/vpn": "vpn",
  "/network-acls": "acls",
  "/flow-logs": "flowLogs",
  "/traffic-mirrors": "trafficMirrors",
  "/content-delivery": "contentDelivery",
  "/video-streaming": "videoStreaming",
  "/shares": "shares",
  "/backup-plans": "backupPlans",
  "/logs": "logs",
  "/agent-gateways": "agentGateways",
  "/agent-gateway-fleet": "agentGateways",
  "/database/parameter-groups": "parameterGroups",
  "/access": "privateAccess",
  "/sase": "sase",
  "/sase/get-started": "sase",
  "/sase/policies": "sase",
  "/sase/connections": "sase",
  "/sase/internet": "sase",
  "/sase/private-access": ["sase", "privateAccess"],
  "/sase/cloud-apps": "sase",
  "/sase/ai": ["sase", "saseAiSecurity"],
  "/sase/ai/apps": ["sase", "saseAiSecurity"],
  "/sase/ai/prompt-protection": ["sase", "saseAiSecurity"],
  "/sase/ai/conversations": ["sase", "saseAiSecurity"],
  "/sase/ai/mcp-servers": ["sase", "saseAiSecurity"],
  "/sase/ai/agent-gateways": ["sase", "agentGateways"],
  "/sase/data/exact": ["sase", "saseDataProtection"],
  "/sase/data/fingerprints": ["sase", "saseDataProtection"],
  // The identity pages are Private access's (SASE-18).
  "/sase/users": ["sase", "privateAccess"],
  "/sase/experience": ["sase", "saseExperience"],
  "/points-of-presence": "sase",
  "/sase/incidents": ["sase", "saseIncidents"],
  "/sase/analytics": ["sase", "saseAnalytics"],
  "/sase/dns": ["sase", "saseDnsProtection"],
  "/sase/firewall": ["sase", "saseFirewall"],
  "/sase/threats": ["sase", "saseThreatProtection"],
  "/sase/settings": "sase",
};

/** Administration's pages that exist in the SASE edition (SASE-30). The others
 *  (Users & access, Machines, Maintenance, Optimization, Platform metrics,
 *  Logs) run the cloud's own services, which the edition does not have. */
const SASE_EDITION_ADMIN_LEAVES: ReadonlySet<string> = new Set(["/points-of-presence"]);

/** The edition keeps the SASE section and Administration's SASE pages. */
function bySaseEdition(routes: MenuRoute[]): MenuRoute[] {
  return routes.flatMap((r) => {
    if (r.path === SASE_CATEGORY) return [r];
    if (r.path !== "/cat/admin" || !r.routes) return [];
    const routes = r.routes.filter((leaf) => leaf.path && SASE_EDITION_ADMIN_LEAVES.has(leaf.path));
    return routes.length ? [{ ...r, routes }] : [];
  });
}

function gateOpen(gate: Gate, features: MenuFeatures): boolean {
  return (Array.isArray(gate) ? gate : [gate]).every((flag) => Boolean(features[flag]));
}

function pruneOptional(routes: MenuRoute[], features: MenuFeatures): MenuRoute[] {
  return routes.flatMap((r) => {
    const gate = r.path ? OPTIONAL_LEAVES[r.path] : undefined;
    if (gate && !gateOpen(gate, features)) return [];
    if (!r.routes) return [r];
    const routes = pruneOptional(r.routes, features);
    // A category with nothing left in it is not shown.
    return routes.length ? [{ ...r, routes }] : [];
  });
}

/** The account's product view (SD-01) applied to the customer categories:
 *  "sase_only" keeps the SASE section alone (the account menu and, for
 *  admins, Administration stay), "cloud_only" drops it. */
function byProductView(routes: MenuRoute[], view: ProductView): MenuRoute[] {
  if (view === "sase_only") return routes.filter((r) => r.path === SASE_CATEGORY);
  if (view === "cloud_only") return routes.filter((r) => r.path !== SASE_CATEGORY);
  return routes;
}

/**
 * Build the ProLayout menu tree for a user. Customers see the self-service
 * categories; admins additionally get the Administration category. Leaves
 * for optional services (auto scaling, VPN, Network ACLs, flow logs, file
 * shares, central logging, SASE) are dropped when
 * `features` says the cloud lacks them. `view` is the account's product view;
 * it applies only while the SASE section exists here (without it, "SASE
 * only" would leave nothing to show). In the SASE edition (`saseEdition`)
 * the view is always "SASE only" and Administration keeps its SASE pages.
 */
export function buildMenuRoutes(
  admin: boolean,
  features: MenuFeatures = ALL_FEATURES,
  view: ProductView = "everything",
): MenuRoute {
  const effective = features.saseEdition ? "sase_only" : features.sase ? view : "everything";
  const customer = byProductView(customerRoutes, effective);
  const base = admin ? [...customer, ...adminRoutes] : customer;
  const edition = features.saseEdition ? bySaseEdition(base) : base;
  return {
    path: "/",
    routes: regroupLoneVpn(pruneOptional(edition, features)),
  };
}

/** A group with one entry is a wasted click: on a cloud without Private
 *  access, "Access" held VPN alone (platform test round 4). There VPN is
 *  listed with the other network services, after DNS; with Private access
 *  offered the two stay together under Access (PA-21). The route is /vpn
 *  either way. */
function regroupLoneVpn(routes: MenuRoute[]): MenuRoute[] {
  const access = routes.find((r) => r.path === "/cat/access");
  const vpn = access?.routes?.length === 1 ? access.routes[0] : undefined;
  if (!vpn || vpn.path !== "/vpn") return routes;
  if (!routes.some((r) => r.path === "/cat/networking")) return routes;
  return routes.flatMap((r) => {
    if (r.path === "/cat/access") return [];
    if (r.path !== "/cat/networking" || !r.routes) return [r];
    const at = r.routes.findIndex((leaf) => leaf.path === "/dns") + 1;
    const leaves = [...r.routes];
    leaves.splice(at > 0 ? at : leaves.length, 0, vpn);
    return [{ ...r, routes: leaves }];
  });
}

/** A menu leaf with the groups it sits in. */
export interface MenuTrail {
  path: string;
  /** The name the sidebar shows ("Policies", not the launcher's "SASE: Policies"). */
  name: string;
  /** Inside the SASE section. */
  sase: boolean;
  /** The groups between the section and the leaf ("AI security"), SASE only:
   *  the cloud's categories are not part of a trail or a title. */
  groups: string[];
}

/** Every navigable leaf with where it sits — the one source for the
 *  breadcrumb trail and the browser-tab title. */
export function menuTrails(): MenuTrail[] {
  const out: MenuTrail[] = [];
  const walk = (routes: MenuRoute[] = [], parents: MenuRoute[]) => {
    for (const r of routes) {
      if (r.routes && r.routes.length > 0) {
        walk(r.routes, [...parents, r]);
        continue;
      }
      if (!r.path || !r.name || r.path === "/") continue;
      const sase = parents[0]?.path === SASE_CATEGORY;
      out.push({
        path: r.path,
        name: r.name,
        sase,
        groups: sase ? parents.slice(1).flatMap((g) => (g.name ? [g.name] : [])) : [],
      });
    }
  };
  walk(buildMenuRoutes(true).routes, []);
  return out;
}

/** The menu leaf a URL belongs to: the longest leaf path it is, or is under. */
export function menuTrailFor(
  pathname: string,
  trails: MenuTrail[] = menuTrails(),
): MenuTrail | undefined {
  let best: MenuTrail | undefined;
  for (const entry of trails) {
    const hit = entry.path === pathname || pathname.startsWith(`${entry.path}/`);
    if (hit && (!best || entry.path.length > best.path.length)) best = entry;
  }
  return best;
}

/** Flat list of every navigable LEAF section ({path, name}) — used to label
 *  visited pages for the recents/favorites launcher. Skips category parents
 *  (the synthetic /cat/* keys) and the Dashboard root. */
export function flattenMenuRoutes(): { path: string; name: string }[] {
  const out: { path: string; name: string }[] = [];
  const walk = (routes: MenuRoute[] = []) => {
    for (const r of routes) {
      if (r.routes && r.routes.length > 0) {
        walk(r.routes);
        continue;
      }
      if (r.path && r.name && r.path !== "/")
        out.push({ path: r.path, name: r.launcherName ?? r.name });
    }
  };
  walk(buildMenuRoutes(true).routes);
  return out;
}

/** Pages reached from the account menu or a link, not the sidebar. */
const OFF_MENU_TITLES: MenuTrail[] = [
  { path: "/account", name: "Account settings" },
  { path: "/credentials", name: "API keys & credentials" },
  { path: "/about/licences", name: "Licences" },
  { path: "/fleet", name: "Fleet" },
].map((entry) => ({ ...entry, sase: false, groups: [] }));

const TITLE_TRAILS: MenuTrail[] = [...menuTrails(), ...OFF_MENU_TITLES];

/** The browser-tab title of a page — one rule for every page, SASE included:
 *  the page's menu name, then "SASE" inside that section, then the product
 *  ("Instances · OmniCloud", "Policies · SASE · OmniCloud"); on a detail page
 *  the resource's name comes first once the page has published one
 *  ("web-1 · Instances · OmniCloud"). The page is the longest menu path the
 *  URL is under. This is the only writer of the title: the layout's own
 *  (`pageTitleRender`) is off, because it wrote the bare menu name over this
 *  one whenever the menu was rebuilt (B22). */
export function documentTitle(pathname: string, resourceName?: string): string {
  const section = menuTrailFor(pathname, TITLE_TRAILS);
  if (!section) return "OmniCloud";
  const parts = section.path === pathname || !resourceName ? [] : [resourceName];
  // A group's own "Overview" is titled by the group ("AI security · SASE").
  const name = section.name === "Overview" ? (section.groups.at(-1) ?? section.name) : section.name;
  return [...parts, name, ...(section.sase ? ["SASE"] : []), "OmniCloud"].join(" · ");
}

/** The SASE pages a built menu offers ({path, name}), for Cmd+K: the menu's
 *  gates and the product view are already applied, so a closed door is not
 *  offered. Names are the launcher's ("SASE: Analytics"). */
export function saseMenuPages(menu: MenuRoute): { path: string; name: string }[] {
  const out: { path: string; name: string }[] = [];
  const walk = (routes: MenuRoute[] = []) => {
    for (const r of routes) {
      if (r.routes?.length) {
        walk(r.routes);
        continue;
      }
      if (r.path && r.name && /^\/sase(\/|$)/.test(r.path))
        out.push({ path: r.path, name: r.launcherName ?? r.name });
    }
  };
  walk(menu.routes);
  return out;
}

/**
 * The one menu leaf to highlight for a path: the longest leaf path that is the
 * path itself or a prefix of it at a segment boundary. ProLayout selects every
 * leaf that prefix-matches, so on `/sase/get-started` the Overview leaf
 * (`/sase`) lit up too (SASE-1 record §5), and every SASE door would have done
 * the same; the same rule stops `/compute/autoscaling-groups` lighting up
 * Instances. `/` matches only itself. Returns undefined off the menu.
 */
export function selectedMenuKey(menu: MenuRoute, pathname: string): string | undefined {
  let best: string | undefined;
  const walk = (routes: MenuRoute[] = []) => {
    for (const r of routes) {
      if (r.routes?.length) {
        walk(r.routes);
        continue;
      }
      const path = r.path;
      if (!path || path.startsWith("/cat/")) continue;
      const hit = path === pathname || (path !== "/" && pathname.startsWith(`${path}/`));
      if (hit && (!best || path.length > best.length)) best = path;
    }
  };
  walk(menu.routes);
  return best;
}
